import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TaskMessageThread from '../components/chat/TaskMessageThread';
import { api } from '../lib/api';
import { useUIStore } from '../store';
import { useConversations } from '../hooks/useConversations';
import { useChatUnread } from '../hooks/useChatUnread';
import { resolveLocale } from '../lib/dateFormat';
import type { GroupConversation, TaskConversation } from '../types/conversations';
import type { User } from '../types';
import GroupChatThread from '../components/chat/GroupChatThread';

// Dedicated /messages page (sidebar entry, mobile-app style
// master-detail). Row rendering is copied from ConversationsPanel's
// renderGroupRow/renderTaskRow/previewOf/initialsOf (same classes,
// avatar/preview/date pattern) — intentionally duplicated, not
// imported, so the header dropdown stays untouched.
const Messages: React.FC = () => {
  const { t } = useTranslation();
  const { theme, language } = useUIStore();
  const { taskConversations, groupConversations, loading, loaded, error } = useConversations(30000);
  const { isUnread, markRead } = useChatUnread(taskConversations, groupConversations, loaded);
  const [selectedGroup, setSelectedGroup] = useState<GroupConversation | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskConversation | null>(null);
  const [members, setMembers] = useState<User[]>([]);
  const [leftWidth, setLeftWidth] = useState(320);
  const [isDragging, setIsDragging] = useState(false);
  const leftPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!leftPanelRef.current) return;
      const newWidth = e.clientX - leftPanelRef.current.getBoundingClientRect().left;
      setLeftWidth(Math.max(240, Math.min(560, newWidth)));
    };
    const handleMouseUp = () => setIsDragging(false);

    document.body.classList.add('select-none', 'cursor-col-resize');
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('select-none', 'cursor-col-resize');
    };
  }, [isDragging]);

  useEffect(() => {
    api
      .get('/workspace/members')
      .then((res) => {
        const data = (res.data as any)?.data;
        if (Array.isArray(data)) setMembers(data);
      })
      .catch(() => {
        // Non-critical — previews just fall back to the raw author id.
      });
  }, []);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const initialsOf = (title: string) => {
    const words = title.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return (words[0]?.slice(0, 2) || '?').toUpperCase();
  };

  const previewOf = (authorId: string, message: string | null) => {
    if (!message) return t('globalChat.noMessages');
    const authorName = members.find((m) => m.id === authorId)?.name || authorId;
    return `${authorName}: ${message}`;
  };

  // Phones show ONE pane at a time (list, or the open conversation with a back
  // bar); md+ keeps the side-by-side master-detail with the draggable divider.
  const hasSelection = !!(selectedGroup || selectedTask);
  const clearSelection = () => { setSelectedGroup(null); setSelectedTask(null); };

  // Whatever conversation is open counts as read — including messages that
  // arrive (via the poll) while it is on screen.
  const openGroupId = selectedGroup?.id;
  const openTaskId = selectedTask?.id;
  useEffect(() => {
    const g = groupConversations.find((c) => c.id === openGroupId);
    if (g) markRead('group', g.id, g.latest_message.created_at);
    const tk = taskConversations.find((c) => c.id === openTaskId);
    if (tk) markRead('task', tk.id, tk.latest_message.created_at);
  }, [openGroupId, openTaskId, groupConversations, taskConversations, markRead]);

  const handleTaskClick = (item: TaskConversation) => {
    setSelectedTask(item);
    setSelectedGroup(null);
  };

  const rowBase = 'w-full text-left flex items-center gap-3 px-4 py-3 border-b transition-colors duration-200';
  const rowTheme = theme === 'dark' ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50';
  const rowCls = (isLast: boolean, isActive: boolean) =>
    `${rowBase} ${rowTheme} ${isLast ? 'border-b-0' : ''} ${isActive ? 'bg-blue-50 dark:bg-gray-700' : ''}`;

  const renderGroupRow = (item: GroupConversation, isLast: boolean) => (
    <button
      key={item.id}
      onClick={() => { setSelectedGroup(item); setSelectedTask(null); }}
      className={rowCls(isLast, selectedGroup?.id === item.id)}
    >
      <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold"
        style={{ backgroundColor: item.color || '#3b5bdb' }}>
        {initialsOf(item.name)}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {item.name}
        </p>
        <p className={`text-xs mt-0.5 truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {previewOf(item.latest_message.author_id, item.latest_message.message)}
        </p>
      </div>
      <span className={`text-xs flex-shrink-0 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
        {formatDate(item.latest_message.created_at)}
      </span>
      {isUnread('group', item) && (
        <span aria-label={t('globalChat.unread')} className="h-2.5 w-2.5 rounded-full bg-red-500 flex-shrink-0" />
      )}
    </button>
  );

  const renderTaskRow = (item: TaskConversation, isLast: boolean) => (
    <button key={item.id} onClick={() => handleTaskClick(item)} className={rowCls(isLast, selectedTask?.id === item.id)}>
      <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold ${
        theme === 'dark' ? 'bg-gray-600 text-white' : 'bg-[#e8f7fa] text-[#0e7490]'}`}>
        {initialsOf(item.title)}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {item.title}
        </p>
        <p className={`text-xs mt-0.5 truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {previewOf(item.latest_message.author_id, item.latest_message.message)}
        </p>
      </div>
      <span className={`text-xs flex-shrink-0 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
        {formatDate(item.latest_message.created_at)}
      </span>
      {isUnread('task', item) && (
        <span aria-label={t('globalChat.unread')} className="h-2.5 w-2.5 rounded-full bg-red-500 flex-shrink-0" />
      )}
    </button>
  );

  // LAYOUT_A: header + left list panel
  const leftPanel = (
    <div ref={leftPanelRef} style={{ '--lw': `${leftWidth}px` } as React.CSSProperties} className={`${hasSelection ? 'hidden md:block' : 'block'} w-full md:w-[var(--lw)] flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 h-[calc(100vh-160px)] [height:calc(100dvh-160px)] overflow-y-auto`}>
      {loading && taskConversations.length === 0 && groupConversations.length === 0 ? (
        <p className={`px-4 py-6 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('common.loading')}
        </p>
      ) : error ? (
        <p className="px-4 py-6 text-center text-sm text-red-500">{error}</p>
      ) : (
        <>
          <p className={`px-4 pt-3 pb-1 text-xs font-bold uppercase tracking-wide ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('globalChat.groupsSection')}
          </p>
          {groupConversations.length === 0 ? (
            <p className={`px-4 py-3 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('globalChat.noMessages')}
            </p>
          ) : (
            groupConversations.map((g, i) => renderGroupRow(g, i === groupConversations.length - 1 && taskConversations.length === 0))
          )}
          <p className={`px-4 pt-3 pb-1 text-xs font-bold uppercase tracking-wide border-t ${theme === 'dark' ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-100'}`}>
            {t('globalChat.taskConversationsSection')}
          </p>
          {taskConversations.length === 0 ? (
            <p className={`px-4 py-3 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('globalChat.noMessages')}
            </p>
          ) : (
            taskConversations.map((item, i) => renderTaskRow(item, i === taskConversations.length - 1))
          )}
        </>
      )}
    </div>
  );
  // LAYOUT_B_BELOW: right detail panel + page return
  const rightPanel = (
    <div className={`${hasSelection ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 h-[calc(100vh-160px)] [height:calc(100dvh-160px)] flex-col overflow-hidden`}>
      {hasSelection && (
        <button
          type="button"
          onClick={clearSelection}
          className="md:hidden flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700 text-left"
        >
          <span aria-hidden="true">←</span> {t('globalChat.back')}
        </button>
      )}
      {selectedGroup ? (
        <GroupChatThread key={selectedGroup.id} group={selectedGroup} />
      ) : selectedTask ? (
        <TaskMessageThread key={selectedTask.id} task={selectedTask} />
      ) : (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            {t('globalChat.selectConversation')}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('common.messages')}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('globalChat.groupsSection')} · {t('globalChat.taskConversationsSection')}
        </p>
      </div>
      <div className="flex gap-4">
        {leftPanel}
        <div
          role="separator"
          aria-orientation="vertical"
          className="hidden md:block w-1.5 cursor-col-resize flex-shrink-0 bg-gray-200 dark:bg-gray-700 hover:bg-blue-400 dark:hover:bg-blue-400 rounded transition-colors"
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            setIsDragging(true);
          }}
        />
        {rightPanel}
      </div>
    </div>
  );
};

export default Messages;
